import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import QRCode from 'react-qr-code';
import { api } from '../api';
import { gerarPixPayload } from '../utils/pixPayload';
import Metrica, { MetricaGrid } from '../components/ui/Metrica';
import Estado from '../components/ui/Estado';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { fmtBRL, fmtInt, fmtData } from '../utils/format';

const WHATSAPP_BASE = 'https://wa.me/5554999487383?text=';
const PIX_CONFIG = {
  chave: '+5554999487383',
  nome: 'ELIARDO MARINI',
  cidade: 'BRASILIA',
};

const dataOuNulo = (iso) => {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const texto = fmtData(iso);
  return texto && texto !== '—' ? texto : null;
};

/** Instrução de identificação do pagamento, reutilizada na página e no modal. */
function InstrucaoIdentificacao({ codigoConvite }) {
  const whatsComprovante = WHATSAPP_BASE + encodeURIComponent(
    `Olá! Segue o comprovante de pagamento da fazenda ${codigoConvite || '(sem código)'}.`
  );
  return (
    <div className="aviso aviso--atencao">
      <strong>Como identificamos seu pagamento:</strong>
      <ul>
        <li>Na descrição do Pix, escreva o código da sua fazenda: <strong className="codigo-fazenda">{codigoConvite || '—'}</strong></li>
        <li>Ou envie o comprovante pelo{' '}
          <a href={whatsComprovante} target="_blank" rel="noopener noreferrer">
            Fale Conosco (WhatsApp)
          </a>
        </li>
      </ul>
    </div>
  );
}

export default function MinhaAssinatura() {
  const [assinatura, setAssinatura] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const carrosselRef = useRef(null);

  // Estado do pagamento Pix inline
  const [mostrarPix, setMostrarPix] = useState(false);
  const [carregandoPix, setCarregandoPix] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [erroCopia, setErroCopia] = useState('');
  const [erroPagamento, setErroPagamento] = useState('');
  const codigoRef = useRef(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [ass, pls] = await Promise.all([api.getAssinatura(), api.getPlanos()]);
      setAssinatura(ass);
      setPlanos(pls || []);
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar sua assinatura.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const fazenda = localStorage.getItem('fazenda') || '';
  const codigoConvite = localStorage.getItem('codigoConvite') || '';

  const montarLinkWhatsapp = (nomePlano) => {
    const msg = `Olá gostaria de mudar o plano da minha fazenda "${fazenda} ${codigoConvite}" para o plano "${nomePlano}"`;
    return WHATSAPP_BASE + encodeURIComponent(msg);
  };

  const scrollCarrossel = (direcao) => {
    if (!carrosselRef.current) return;
    const larguraItem = carrosselRef.current.firstChild?.offsetWidth || 260;
    carrosselRef.current.scrollBy({ left: direcao * (larguraItem + 16), behavior: 'smooth' });
  };

  const limites = assinatura ? [
    { label: 'Usuários', value: assinatura.maxUsuarios },
    { label: 'Safras', value: assinatura.maxSafras },
    { label: 'Lavouras', value: assinatura.maxLavouras },
    { label: 'Armazéns', value: assinatura.maxArmazens },
    { label: 'Áreas por safra', value: assinatura.maxVinculosSafra },
    { label: 'Livro Caixa', value: assinatura.livroCaixa === true ? 'Sim' : 'Não' },
  ] : [];

  const planoAtual = assinatura?.plano || 'Básico';
  const fmtValor = (v) => v != null ? v.toFixed(2).replace('.', ',') : '0,00';

  // Pagamento
  const status = assinatura?.status;
  const valorMensal = assinatura?.valorMensal;
  const valorDevido = assinatura?.valorDevido;
  const podePagar = status === 'VENCIDO' || (assinatura?.diasRestantes != null && assinatura.diasRestantes <= 7);
  const valorPagar = status === 'VENCIDO' ? (valorDevido || valorMensal) : valorMensal;
  const modoPagamento = assinatura?.modoPagamento;

  const pixPayload = useMemo(() => {
    if (!valorPagar || !assinatura?.fazendaId) return '';
    return gerarPixPayload({
      ...PIX_CONFIG,
      valor: valorPagar,
      txid: `AGR${String(assinatura.fazendaId || 0).padStart(3, '0')}`,
    });
  }, [valorPagar, assinatura?.fazendaId]);

  const handlePagar = async () => {
    setCarregandoPix(true);
    setErroPagamento('');
    if (modoPagamento === 'stripe') {
      try {
        const successUrl = window.location.origin + '/?pagamento=sucesso';
        const cancelUrl = window.location.origin + '/?pagamento=cancelado';
        const { url } = await api.criarCheckout(successUrl, cancelUrl);
        window.location.href = url;
      } catch (err) {
        setErroPagamento(err.message || 'Erro ao iniciar pagamento.');
        setCarregandoPix(false);
      }
    } else {
      setTimeout(() => { setCarregandoPix(false); setMostrarPix(true); }, 600);
    }
  };

  const copiarSelecionando = () => {
    const el = codigoRef.current;
    if (!el || !window.getSelection) return false;
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return document.execCommand('copy');
    } catch { return false; }
  };

  const copiar = async () => {
    setErroCopia('');
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pixPayload);
        ok = true;
      }
    } catch { ok = false; }
    if (!ok) ok = copiarSelecionando();
    if (ok) { setCopiado(true); setTimeout(() => setCopiado(false), 3000); }
    else { setCopiado(false); setErroCopia('Não conseguimos copiar automaticamente. O código acima está selecionável: copie à mão e cole no app do banco.'); }
  };

  const dataVenceEm = dataOuNulo(assinatura?.venceEm);
  const dataVenceuEm = dataOuNulo(assinatura?.venceuEm);

  return (
    <div>
      <PageHeader
        titulo="Minha assinatura"
        icone="assinatura"
        descricao="Plano, limites e pagamento"
      />
      <div className="card">
        <Estado carregando={carregando} erro={erro} aoTentarNovamente={carregar} skeleton="card" itens={3}>
          {assinatura && (
            <>
              <div className="plano-atual">
                <div className="plano-atual__rotulo">Plano atual</div>
                <div className="plano-atual__nome">{planoAtual}</div>
                <div className="plano-atual__preco">{fmtBRL(valorMensal)} / mês</div>
                {assinatura.descricaoPlano && (
                  <div className="plano-atual__desc">{assinatura.descricaoPlano}</div>
                )}
                <div className="plano-atual__status">
                  {status === 'TRIAL' && (
                    <span className="selo selo--info">
                      Período gratuito{dataVenceEm ? ` até ${dataVenceEm}` : ''}
                      {assinatura.diasRestantes != null ? ` (${assinatura.diasRestantes} dias)` : ''}
                    </span>
                  )}
                  {status === 'ATIVO' && (
                    <span className="selo selo--ok">
                      Ativa{dataVenceEm ? ` — próximo pagamento até ${dataVenceEm}` : ''}
                    </span>
                  )}
                  {status === 'VENCIDO' && (
                    <span className="selo selo--perigo">
                      <Icon nome="alerta" tamanho={14} />
                      Vencida{dataVenceuEm ? ` desde ${dataVenceuEm}` : ''}
                      {assinatura.mesesDevendo ? ` · ${assinatura.mesesDevendo} mês(es) em atraso` : ''}
                    </span>
                  )}
                </div>
              </div>

              <div className="secao-titulo">
                <h2>Limites do seu plano</h2>
              </div>
              <MetricaGrid min={130}>
                {limites.map(l => (
                  <Metrica key={l.label} label={l.label} value={typeof l.value === 'string' ? l.value : fmtInt(l.value)} />
                ))}
              </MetricaGrid>
            </>
          )}
        </Estado>
      </div>

      {/* Seção de pagamento */}
      {assinatura && (
        <div className="card">
          <div className="secao-titulo"><h2>Pagamento</h2></div>

          <div className="pix-caixa-acao">
            {erroPagamento && <div className="msg msg-error">{erroPagamento}</div>}

            <button
              type="button"
              onClick={handlePagar}
              disabled={!podePagar || carregandoPix || mostrarPix}
              className={`${podePagar ? 'btn-primary' : 'btn-secondary'} botao-largo botao-largo--pago`}
            >
              {carregandoPix ? 'Carregando...' : mostrarPix ? 'Dados de pagamento abaixo' : podePagar ? `Pagar R$ ${fmtValor(valorPagar)} via Pix` : 'Pagamento disponível 7 dias antes do vencimento'}
            </button>

            {podePagar && !mostrarPix && (
              <p className="secao-sub">
                {modoPagamento === 'stripe' ? 'Você será redirecionado para a página segura de pagamento.' : 'O QR Code e o código Pix serão exibidos abaixo.'}
              </p>
            )}
          </div>

          {mostrarPix && modoPagamento !== 'stripe' && (
            <div className="pix-caixa">
              <div className="pix-caixa__qr">
                <div className="pix-caixa__qr-fundo">
                  <QRCode value={pixPayload} size={160} />
                </div>
              </div>
              <div className="pix-caixa__hint">Ou copie o código Pix:</div>
              <div
                ref={codigoRef}
                onClick={copiar}
                className="pix-codigo"
                title="Clique para copiar"
              >
                {pixPayload}
              </div>
              <button
                type="button"
                onClick={copiar}
                className={`pix-copiar ${copiado ? 'pix-copiar--ok' : ''}`}
              >
                <Icon nome={copiado ? 'ok' : 'copiar'} tamanho={14} />
                {copiado ? 'Copiado!' : 'Clique para copiar'}
              </button>
              {erroCopia && (
                <div className="msg msg-error" role="alert">{erroCopia}</div>
              )}
              <div className="pix-caixa__prazo">
                <Icon nome="relogio" tamanho={14} />
                O pagamento pode levar até 6 horas para ser identificado.
              </div>
            </div>
          )}

          {/* Instrução de identificação */}
          <InstrucaoIdentificacao codigoConvite={codigoConvite} />
        </div>
      )}

      {/* Carrossel de planos disponíveis */}
      {planos.length > 0 && (
        <div className="card">
          <div className="secao-titulo">
            <h2>Planos disponíveis</h2>
          </div>
          <p className="secao-sub">
            Altere seu plano para outro disponível conversando com nossa equipe.
          </p>

          <div className="planos-carrossel">
            {planos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => scrollCarrossel(-1)}
                  aria-label="Plano anterior"
                  className="planos-carrossel__seta planos-carrossel__seta--esq"
                ><Icon nome="chevron-direita" tamanho={16} /></button>
                <button
                  type="button"
                  onClick={() => scrollCarrossel(1)}
                  aria-label="Próximo plano"
                  className="planos-carrossel__seta planos-carrossel__seta--dir"
                ><Icon nome="chevron-direita" tamanho={16} /></button>
              </>
            )}

            <div ref={carrosselRef} className="planos-carrossel__faixa">
              {planos.map(p => {
                const atual = p.nome === planoAtual;
                return (
                  <div key={p.id} className={`plano-card ${atual ? 'plano-card--atual' : ''}`}>
                    {atual && <span className="selo selo--ok">Seu plano</span>}
                    <div className="plano-card__nome">{p.nome}</div>
                    <div className="plano-card__preco">
                      {fmtBRL(p.valorMensal)}<span> /mês</span>
                    </div>
                    {p.descricao && <div className="plano-card__desc">{p.descricao}</div>}
                    <div className="plano-card__lista">
                      <div><Icon nome="usuarios" tamanho={14} /> {p.maxUsuarios} usuário{p.maxUsuarios > 1 ? 's' : ''}</div>
                      <div><Icon nome="safra" tamanho={14} /> {p.maxSafras} safra{p.maxSafras > 1 ? 's' : ''}</div>
                      <div><Icon nome="lavoura" tamanho={14} /> {p.maxLavouras} área{p.maxLavouras > 1 ? 's' : ''} de cultivo</div>
                      <div><Icon nome="armazem" tamanho={14} /> {p.maxArmazens} armazé{p.maxArmazens > 1 ? 'ns' : 'm'}</div>
                      <div><Icon nome="semente" tamanho={14} /> {p.maxVinculosSafra} área{p.maxVinculosSafra > 1 ? 's' : ''} por safra</div>
                      <div><Icon nome="livro-caixa" tamanho={14} /> Livro caixa: {p.livroCaixa ? 'Sim' : 'Não'}</div>
                    </div>
                    <div className="plano-card__rodape">
                      {atual ? (
                        <span className="plano-card__ativo">
                          <Icon nome="ok" tamanho={14} /> Plano ativo
                        </span>
                      ) : (
                        <a
                          href={montarLinkWhatsapp(p.nome)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-whatsapp"
                        >
                          Mudar para este plano
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="aviso aviso--info">
          Precisa de um plano personalizado ou tem dúvidas?{' '}
          <a
            href={WHATSAPP_BASE + encodeURIComponent('Olá! Gostaria de falar sobre o AgriGest')}
            target="_blank"
            rel="noopener noreferrer"
          >
            Fale conosco pelo WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

export { InstrucaoIdentificacao };
